/**
 * TourTooltip
 *
 * A floating Card that displays the current tour step.  It positions itself
 * relative to the target element, tries the preferred placement first, and
 * falls back to alternatives when the preferred placement would overflow the
 * viewport.  Center-placement steps use a fixed, centred position.
 */

import { useEffect, useState, useRef } from 'react';
import { X, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import type { TourStep } from '@/types/product-tour';

interface TooltipPosition {
  top: number;
  left: number;
  transform?: string;
}

const TOOLTIP_WIDTH = 400;
const TOOLTIP_APPROX_HEIGHT = 260;
const VIEWPORT_PADDING = 16;

type Placement = 'top' | 'bottom' | 'left' | 'right' | 'center';

function computePosition(
  target: Element | null,
  preferredPlacement: Placement,
): TooltipPosition {
  // Center: fixed screen centre
  if (!target || preferredPlacement === 'center') {
    return {
      top: window.innerHeight / 2,
      left: window.innerWidth / 2,
      transform: 'translate(-50%, -50%)',
    };
  }

  const r = target.getBoundingClientRect();
  const gap = 16; // gap between element and tooltip

  const placements: Placement[] = [
    preferredPlacement,
    'bottom',
    'top',
    'right',
    'left',
  ];

  for (const placement of placements) {
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'bottom':
        top  = r.bottom + window.scrollY + gap;
        left = r.left + window.scrollX + r.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case 'top':
        top  = r.top + window.scrollY - TOOLTIP_APPROX_HEIGHT - gap;
        left = r.left + window.scrollX + r.width / 2 - TOOLTIP_WIDTH / 2;
        break;
      case 'right':
        top  = r.top + window.scrollY + r.height / 2 - TOOLTIP_APPROX_HEIGHT / 2;
        left = r.right + window.scrollX + gap;
        break;
      case 'left':
        top  = r.top + window.scrollY + r.height / 2 - TOOLTIP_APPROX_HEIGHT / 2;
        left = r.left + window.scrollX - TOOLTIP_WIDTH - gap;
        break;
    }

    // Clamp to viewport
    const clampedLeft = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_PADDING),
    );
    const clampedTop = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, window.innerHeight + window.scrollY - TOOLTIP_APPROX_HEIGHT - VIEWPORT_PADDING),
    );

    // Accept first placement that doesn't overlap the target element
    const overlapsTarget =
      clampedLeft < r.right + window.scrollX + gap &&
      clampedLeft + TOOLTIP_WIDTH > r.left + window.scrollX - gap &&
      clampedTop < r.bottom + window.scrollY + gap &&
      clampedTop + TOOLTIP_APPROX_HEIGHT > r.top + window.scrollY - gap;

    if (!overlapsTarget) {
      return { top: clampedTop, left: clampedLeft };
    }
  }

  // Fallback: centre of screen
  return {
    top: window.innerHeight / 2,
    left: window.innerWidth / 2,
    transform: 'translate(-50%, -50%)',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface TourTooltipProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onClose: () => void;
}

export function TourTooltip({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  onClose,
}: TourTooltipProps) {
  const [pos, setPos] = useState<TooltipPosition>({
    top: window.innerHeight / 2,
    left: window.innerWidth / 2,
    transform: 'translate(-50%, -50%)',
  });

  const stepNum   = currentStep + 1;
  const isFirst   = currentStep === 0;
  const isLast    = currentStep === totalSteps - 1;
  const progress  = Math.round((stepNum / totalSteps) * 100);
  const placement = step.placement ?? 'bottom';
  const isCenter  = placement === 'center' || !step.target;

  useEffect(() => {
    function reposition() {
      const target = step.target ? document.querySelector(step.target) : null;
      setPos(computePosition(target, placement));
    }

    reposition();
    window.addEventListener('scroll', reposition, { passive: true });
    window.addEventListener('resize', reposition, { passive: true });
    return () => {
      window.removeEventListener('scroll', reposition);
      window.removeEventListener('resize', reposition);
    };
  }, [step.target, placement]);

  const posStyle: React.CSSProperties = isCenter
    ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    : { position: 'absolute', top: pos.top, left: pos.left, transform: pos.transform };

  return (
    <div
      data-tour-tooltip
      style={{
        ...posStyle,
        zIndex: 10001,
        width: TOOLTIP_WIDTH,
        maxWidth: '90vw',
        pointerEvents: 'auto',
      }}
      className="animate-in fade-in zoom-in-95 duration-200"
    >
      <Card className="shadow-2xl border border-border bg-background">
        <CardHeader className="pb-2 pt-4 px-4">
          {/* Celebration header on last step */}
          {isLast && (
            <div className="flex items-center gap-2 mb-2 text-teal-500">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span className="text-xs font-medium uppercase tracking-wide">Tour Complete</span>
            </div>
          )}

          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-sm font-semibold leading-snug">{step.title}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Step {stepNum} of {totalSteps}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 -mt-0.5 -mr-1 text-muted-foreground hover:text-foreground"
              onClick={onClose}
            >
              <X className="h-3.5 w-3.5" />
              <span className="sr-only">Close tour</span>
            </Button>
          </div>

          {/* Progress bar */}
          <Progress value={progress} className="h-1 mt-2" />
        </CardHeader>

        <CardContent className="px-4 pb-4 pt-1 space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>

          {/* Optional action button */}
          {step.action && (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={step.action.onClick}
            >
              {step.action.label}
            </Button>
          )}

          {/* Footer navigation */}
          <div className="flex items-center justify-between gap-2">
            {/* Previous */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              onClick={onPrevious}
              disabled={isFirst}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </Button>

            <div className="flex items-center gap-2">
              {/* Skip */}
              {!isLast && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-muted-foreground"
                  onClick={onSkip}
                >
                  Skip tour
                </Button>
              )}

              {/* Next / Finish */}
              <Button
                size="sm"
                className="h-8 gap-1"
                onClick={onNext}
              >
                {isLast ? 'Finish' : 'Next'}
                {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
